import { AppManagerBillingPage } from '@';

// --- Constants ---
export const SDK_LOG_PREFIX = '[AppManager SDK]';
export const EVERY_30_DAYS = 'EVERY_30_DAYS';
export const ANNUAL = 'ANNUAL';
export const FEATURES_API_ENDPOINT = '/api/app-manager/plan-features';
export const PLANS_API_ENDPOINT = '/api/app-manager/plans';
export const PROCESS_PLAN_API_ENDPOINT = '/api/app-manager/plan/process';

export const INTERVAL_MAP = {
    [EVERY_30_DAYS]: { label: 'Monthly', short: 'mo' },
    [ANNUAL]: { label: 'Yearly', short: 'yr' }
};

// --- General Utility Functions ---
/** Deep equality check for objects/arrays */
export function deepEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
}

/** Safe JSON parse with fallback */
export function safeParse(value, fallback = {}) {
    try { return JSON.parse(value); } catch { return fallback; }
}

/** Emit custom event from an element */
export function emitEvent(element, name, detail = {}) {
    element.dispatchEvent(new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true,
    }));
}

// --- Translation Helpers ---
/** Translate a string using AppManagerBillingPage translations */
export function translateMe(key) {
    return AppManagerBillingPage.translateMe(key);
}

// --- Interval Helpers ---
export function getIntervalLabel(interval) {
    return INTERVAL_MAP[interval]?.label || 'Monthly';
}
export function getIntervalShortLabel(interval) {
    return INTERVAL_MAP[interval]?.short || 'mo';
}

// --- Plan/Feature Logic ---
/**
 * Calculate plan price details including discounts
 * @param {Object} plan - The plan object containing price and discount information
 * @param {Object|null} promotionalDiscount - Optional promotional discount to apply
 * @returns {Object} - Plan object with calculated price details
 */
export function calculatePlanPriceWithDiscounts(plan, promotionalDiscount = null) {
    try {
        const originalPrice = parseFloat(plan.price?.toString?.() ?? plan.price);
        if (isNaN(originalPrice)) {
            console.error(SDK_LOG_PREFIX, 'Invalid plan price:', plan.price);
            return { ...plan, price: 0, strike_price: null, has_discount: false };
        }
        let finalPrice = originalPrice;
        let hasDiscount = false;
        if (
            promotionalDiscount
            && promotionalDiscount?.value > 0
            && Array.isArray(promotionalDiscount.plan_relation)
            && promotionalDiscount.plan_relation.includes(plan.id)
        ) {
            hasDiscount = true;
            finalPrice = promotionalDiscount.type === 'percentage'
                ? originalPrice - (originalPrice * promotionalDiscount.value) / 100
                : Math.max(0, originalPrice - promotionalDiscount.value);
        } else if (plan.discount > 0) {
            hasDiscount = true;
            finalPrice = plan.discount_type === 'percentage'
                ? originalPrice - (originalPrice * plan.discount) / 100
                : Math.max(0, originalPrice - plan.discount);
        }
        return {
            ...plan,
            price: parseFloat(finalPrice.toFixed(2)),
            strike_price: hasDiscount ? parseFloat(originalPrice.toFixed(2)) : null,
            has_discount: hasDiscount
        };
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in calculatePlanPriceWithDiscounts:', e, plan, promotionalDiscount);
        return { ...plan, price: 0, strike_price: null, has_discount: false };
    }
}

/**
 * Check if the given plan is the current active plan.
 * This returns true only when there is an active charge, a currentPlan exists,
 * and the plan either matches the currentPlan id or (for non-custom plans)
 * its base_plan matches the currentPlan id. Ensures "current" is tied to an active charge.
 */
export function isCurrentPlan(plan, currentPlan, hasActiveCharge = false) {
    return hasActiveCharge
        && currentPlan
        && (
            currentPlan.id === plan.id
            || (
                !plan.is_custom
                && plan.base_plan === currentPlan.id
            )
        );
}

/** Check if the plan is the same in another interval */
export function isSamePlanInOtherInterval(shopifyPlan, plan, currentPlan) {
    return currentPlan && (plan.shopify_plans === shopifyPlan);
}

/**
 * Determine whether to show a store-base plan note below the plan.
 * Only applicable for store-base plans (`plan.store_base_plan`).
 * The note is shown when:
 * - a `currentPlan` exists,
 * - the `shopifyPlan` is listed in `plan.shopify_plans`,
 * - the plan is not the current active plan,
 * - and the plan is not the same plan in another interval.
 *
 * Use this to display the following note below the plan:
 * Note: On account of your recent Shopify plan upgrade, you should consider upgrading your current app plan
 */
export function isPlanWithNote(shopifyPlan, plan, currentPlan, hasActiveCharge) {
    return !!(
        plan.store_base_plan
        && currentPlan
        && plan.shopify_plans.includes(shopifyPlan)
        && !isCurrentPlan(plan, currentPlan, hasActiveCharge)
        && !isSamePlanInOtherInterval(shopifyPlan, plan, currentPlan)
    );
}

/**
 * Format a feature value for display
 * @param {Object} feature - Feature object
 * @param {Object} featureValues - Optional feature values
 * @returns {string}
 */
export function formatFeature(feature, featureValues) {
    try {
        if (["double", "integer"].includes(feature?.value_type)) {
            if (feature.format === "percentage") {
                return `${feature.value}%`;
            } else if (feature.format === "count") {
                return feature.value < 0 ? translateMe("Unlimited") : feature.value;
            } else {
                return translateMe(feature.value);
            }
        }
        if (feature?.value_type === "array") {
            let values = [];
            try {
                if (Array.isArray(feature.value)) {
                    values = feature.value;
                } else if (typeof feature.value === "string") {
                    values = JSON.parse(feature.value);
                }
            } catch (e) {
                console.error(SDK_LOG_PREFIX, "Error parsing array feature value:", e, feature.value);
                return "";
            }
            return Array.isArray(values) ? values.join(",") : "";
        }
        if (feature?.value_type === "boolean") {
            return feature.value ? translateMe("Yes") : translateMe("No");
        }
        if (feature?.value_type === "string") {
            const cleanValue = typeof feature.value === "string"
                ? feature.value.replace(/"/g, "")
                : "";
            return translateMe(cleanValue);
        }
        return "";
    } catch (e) {
        console.error(SDK_LOG_PREFIX, "Error in formatFeature:", e, feature, featureValues);
        return "";
    }
}

/**
 * Determine if a plan button should be disabled
 * @param {string} shopifyPlan
 * @param {Object} plan
 * @param {boolean} hasActiveCharge
 * @param {Object} currentPlan
 * @returns {boolean}
 */
export function isPlanButtonDisabled(shopifyPlan, plan, hasActiveCharge, currentPlan) {
    if (isCurrentPlan(plan, currentPlan, hasActiveCharge)) {
        return true;
    }

    return !!(plan.store_base_plan && !plan.shopify_plans.includes(shopifyPlan));
}

/**
 * Get the button text for a plan
 * @param {string} shopifyPlan
 * @param {Object} plan
 * @param {Object} currentPlan
 * @param {boolean} isPlanTable
 * @param {boolean} hasActiveCharge
 * @returns {string}
 */
export function getPlanButtonText(shopifyPlan, plan, currentPlan, isPlanTable = false, hasActiveCharge = false) {
    const isCurrentPlanFlag = isCurrentPlan(plan, currentPlan, hasActiveCharge);
    if (isCurrentPlanFlag) {
        return translateMe("Selected plan");
    }

    if (plan.store_base_plan && !plan.shopify_plans.includes(shopifyPlan)) {
        return translateMe("Not applicable");
    }

    return translateMe("Choose plan");
}

/**
 * Filter, map, and sort plans based on interval, discount, and note logic.
 * @param {Array} plans - Array of plan objects
 * @param {string} interval - Interval constant (e.g. EVERY_30_DAYS, ANNUAL)
 * @param {object} promotionalDiscount - Discount object
 * @param {string} shopifyPlan - Shopify plan name
 * @param {object} currentPlan - Current plan object
 * @param {boolean} hasActiveCharge - Whether there is an active charge
 * @returns {{anyPlanHasNote: boolean, filteredPlans: {price: number, has_discount: boolean, strike_price: (number|null)}[], anyPlanHasDiscount: boolean}} Filtered, mapped, and sorted plans
 */
export function getFilteredSortedPlans(plans, interval, promotionalDiscount, shopifyPlan, currentPlan, hasActiveCharge) {
    let anyPlanHasDiscount = false;
    let anyPlanHasNote = false;
    let filteredPlans;
    try {
        filteredPlans = (Array.isArray(plans) ? plans : []).filter(plan => plan.interval === interval)
            .map(plan => {
                const planDetails = calculatePlanPriceWithDiscounts(plan, promotionalDiscount);
                if (planDetails.has_discount && !anyPlanHasDiscount) {
                    anyPlanHasDiscount = true;
                }
                if (
                    plan.store_base_plan &&
                    !anyPlanHasNote &&
                    isPlanWithNote(shopifyPlan, plan, currentPlan, hasActiveCharge)
                ) {
                    anyPlanHasNote = true;
                }
                return planDetails;
            })
            .sort((a, b) => a.price - b.price);
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in getFilteredSortedPlans:', e, plans);
        filteredPlans = [];
    }
    return { filteredPlans, anyPlanHasDiscount, anyPlanHasNote };
}

export function formatString (value) {
    if (typeof value === 'string') {
        return value.toLowerCase().replace(/\s+/g, '-');
    }
    return value;
}